/**
 * RatingModal
 * 
 * 사용자 별점 평가 수집 모달
 * - 비로그인 유저 포함 모든 사용자 대상
 * - localStorage로 상태 관리
 */

import { useState } from 'react';
import { useLanguage } from '../i18n';
import { analytics } from '../analytics';

interface RatingModalProps {
  onClose: () => void;
  onSubmit: (rating: number, feedback?: string) => void;
  onSkip: () => void;
}

// 별점 상태 저장 키
const RATING_STORAGE_KEY = 'tristepflow_rating';

interface RatingState {
  hasRated: boolean;
  rating?: number;
  skipUntilNext: boolean;
  neverAsk: boolean;
}

// 저장된 상태 가져오기
export function getRatingState(): RatingState {
  try {
    const stored = localStorage.getItem(RATING_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // 파싱 실패 시 기본값 반환
  }
  return {
    hasRated: false,
    skipUntilNext: false,
    neverAsk: false,
  };
}

// 상태 저장
export function setRatingState(state: Partial<RatingState>): void {
  const current = getRatingState();
  const updated = { ...current, ...state };
  localStorage.setItem(RATING_STORAGE_KEY, JSON.stringify(updated));
}

// 다음 평가까지 스킵 (결과를 볼 때마다 리셋)
export function resetSkipUntilNext(): void {
  const current = getRatingState();
  if (current.skipUntilNext) {
    setRatingState({ skipUntilNext: false });
  }
}

// 별점 모달을 보여줘야 하는지 확인
export function shouldShowRatingModal(): boolean {
  const state = getRatingState();
  
  // 이미 평점을 줬으면 안 보여줌
  if (state.hasRated) return false;
  
  // "다시 묻지 않기"를 선택했으면 안 보여줌
  if (state.neverAsk) return false;
  
  // "나중에"를 눌렀으면 이번에는 안 보여줌 (다음 결과에서 보여줌)
  if (state.skipUntilNext) return false;
  
  return true;
}

function RatingModal({ onClose, onSubmit, onSkip }: RatingModalProps) {
  const { t } = useLanguage();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedback, setFeedback] = useState('');
  const [step, setStep] = useState<'rating' | 'feedback'>('rating');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 0.5 단위 별점 클릭 핸들러
  const handleHalfStarClick = (starIndex: number, isRightHalf: boolean) => {
    const value = isRightHalf ? starIndex : starIndex - 0.5;
    setRating(value);
    // 별점 선택 후 피드백 단계로 이동
    setStep('feedback');
  };

  // 0.5 단위 호버 핸들러
  const handleHalfStarHover = (starIndex: number, isRightHalf: boolean) => {
    const value = isRightHalf ? starIndex : starIndex - 0.5;
    setHoveredRating(value);
  };

  const handleSubmit = async () => {
    if (rating === 0) return;
    
    setIsSubmitting(true);
    
    try {
      // GA4 이벤트 전송
      analytics.ratingSubmit(rating, feedback.trim().length > 0);
      
      // 상태 저장
      setRatingState({
        hasRated: true,
        rating,
        skipUntilNext: false,
      });
      
      onSubmit(rating, feedback.trim() || undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    // "나중에" - 다음 결과까지 스킵
    setRatingState({ skipUntilNext: true });
    analytics.ratingSkip('later');
    onSkip();
  };

  const handleNeverAsk = () => {
    // "다시 묻지 않기"
    setRatingState({ neverAsk: true });
    analytics.ratingSkip('never');
    onClose();
  };

  const displayRating = hoveredRating || rating;

  // 별 렌더링 함수 (0.5 단위 지원)
  const renderStar = (starIndex: number) => {
    const isFull = displayRating >= starIndex;
    const isHalf = !isFull && displayRating >= starIndex - 0.5;

    return (
      <div key={starIndex} className="relative text-4xl cursor-pointer select-none leading-none" style={{ width: '1.2em', height: '1.2em' }}>
        {/* 베이스 빈 별 (grayscale 처리) */}
        <span className="absolute left-0 top-0 grayscale opacity-40">⭐</span>
        
        {/* 채워진 부분 (절반 또는 전체) - 이모지 여백 보정으로 42% 사용 */}
        <span 
          className="absolute left-0 top-0 overflow-hidden"
          style={{ width: isFull ? '100%' : isHalf ? '42%' : '0%' }}
        >
          ⭐
        </span>
        
        {/* 왼쪽 반 클릭 영역 (0.5점) */}
        <button
          className="absolute left-0 top-0 w-1/2 h-full opacity-0 hover:opacity-0 focus:outline-none"
          onClick={() => handleHalfStarClick(starIndex, false)}
          onMouseEnter={() => handleHalfStarHover(starIndex, false)}
          onMouseLeave={() => setHoveredRating(0)}
          aria-label={`${starIndex - 0.5}${t.rating.starLabel}`}
        />
        
        {/* 오른쪽 반 클릭 영역 (1점) */}
        <button
          className="absolute right-0 top-0 w-1/2 h-full opacity-0 hover:opacity-0 focus:outline-none"
          onClick={() => handleHalfStarClick(starIndex, true)}
          onMouseEnter={() => handleHalfStarHover(starIndex, true)}
          onMouseLeave={() => setHoveredRating(0)}
          aria-label={`${starIndex}${t.rating.starLabel}`}
        />
      </div>
    );
  };

  // 별점 설명 텍스트
  const getRatingDescription = (value: number) => {
    if (value <= 1) return t.rating.rating1;
    if (value <= 2) return t.rating.rating2;
    if (value <= 3) return t.rating.rating3;
    if (value <= 4) return t.rating.rating4;
    return t.rating.rating5;
  };

  // 피드백 화면에서 별 표시
  const renderDisplayStars = (value: number) => {
    return [1, 2, 3, 4, 5].map((star) => {
      const isFull = value >= star;
      const isHalf = !isFull && value >= star - 0.5;
      return (
        <span key={star} className="relative inline-block text-2xl leading-none" style={{ width: '1.2em', height: '1.2em' }}>
          <span className="absolute left-0 top-0 grayscale opacity-40">⭐</span>
          <span 
            className="absolute left-0 top-0 overflow-hidden"
            style={{ width: isFull ? '100%' : isHalf ? '42%' : '0%' }}
          >
            ⭐
          </span>
        </span>
      );
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="bg-terminal-bg border border-terminal-border rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'rating' ? (
          <>
            {/* 헤더 - 설득력 있는 메시지 */}
            <div className="text-center mb-6">
              <div className="text-3xl mb-3">⭐</div>
              <h3 className="text-lg font-semibold text-terminal-text mb-2">
                {t.rating.title}
              </h3>
              <p className="text-sm text-terminal-muted">
                {t.rating.subtitle}
              </p>
            </div>

            {/* 별점 선택 (0.5 단위) */}
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => renderStar(star))}
            </div>

            {/* 현재 점수 표시 - 항상 공간 유지 */}
            <div className={`text-center text-lg font-semibold text-status-go mb-2 h-7 ${displayRating > 0 ? 'opacity-100' : 'opacity-0'}`}>
              {displayRating > 0 ? displayRating.toFixed(1) : '0.0'}
            </div>

            {/* 별점 설명 - 항상 공간 유지 */}
            <div className={`text-center text-sm text-terminal-dim mb-6 h-5 ${displayRating > 0 ? 'opacity-100' : 'opacity-0'}`}>
              <span>{displayRating > 0 ? getRatingDescription(displayRating) : '\u00A0'}</span>
            </div>

            {/* 하단 버튼들 */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSkip}
                className="text-sm text-terminal-muted hover:text-terminal-text transition-colors py-2"
              >
                {t.rating.later}
              </button>
              <button
                onClick={handleNeverAsk}
                className="text-xs text-terminal-dim hover:text-terminal-muted transition-colors"
              >
                {t.rating.neverAsk}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* 피드백 단계 */}
            <div className="text-center mb-4">
              <div className="text-2xl mb-1">
                {renderDisplayStars(rating)}
              </div>
              <div className="text-lg font-semibold text-status-go mb-2">
                {rating.toFixed(1)}
              </div>
              <h3 className="text-lg font-semibold text-terminal-text mb-1">
                {rating >= 4 ? t.rating.thankYouHigh : t.rating.thankYouLow}
              </h3>
              <p className="text-sm text-terminal-muted">
                {rating >= 4 ? t.rating.feedbackPromptHigh : t.rating.feedbackPromptLow}
              </p>
            </div>

            {/* 피드백 입력 */}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t.rating.feedbackPlaceholder}
              className="w-full h-24 p-3 bg-terminal-input border border-terminal-border rounded text-terminal-text text-sm placeholder:text-terminal-dim resize-none focus:outline-none focus:border-status-go/50 mb-4"
              autoFocus
            />

            {/* 제출 버튼들 */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep('rating')}
                className="flex-1 py-2 rounded text-sm border border-terminal-border text-terminal-muted hover:border-terminal-text hover:text-terminal-text transition-colors"
              >
                {t.rating.back}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 py-2 rounded text-sm bg-status-go text-terminal-bg font-medium hover:bg-status-go/90 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? t.rating.submitting : t.rating.submit}
              </button>
            </div>

            {/* 스킵 옵션 */}
            <button
              onClick={handleSubmit}
              className="w-full text-xs text-terminal-dim hover:text-terminal-muted transition-colors mt-3"
            >
              {t.rating.skipFeedback}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default RatingModal;

